import { gql, useMutation, useQuery } from '@apollo/client';
import { EntityTypes, NavigationStyles } from '@generated/graphql';
import type { User } from '@interfaces/user';
import {
  CREATE_ENTITY as CREATE_NON_INDIVIDUAL_ENTITY,
  UserContext,
} from '@providers/userContextProvider';
import { delay } from '@utils/common';
import { useSearchParams } from '@utils/usePageState';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../../../../hooks/useLocalization';
import NonIndividualInformationVisual from './nonIndividualSetup.visual';

const FETCH_USER = gql`
  query fetchUser($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        primaryEmail
        language
        organization {
          id
        }
      }
    }
  }
`;

export const NonIndividualSetup = ({
  options,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletionId: string;
  workflowCompletion?: any;
}) => {
  const { languageName } = useLocalization();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [params, setSearchParams] = useSearchParams();
  const { userContext, setActiveEntity, refetch } = useContext(UserContext);

  const { data, loading: fetchLoading } = useQuery(FETCH_USER, {
    variables: { userId },
    fetchPolicy: 'no-cache',
  });
  const [createEntity, { loading: createEntityLoading }] = useMutation(
    CREATE_NON_INDIVIDUAL_ENTITY,
  );
  const createNonIndividualEntity = async () => {
    const user: Partial<User> = data?.fetchUser?.user;
    setLoading(true);

    const { data: resp } = await createEntity({
      variables: {
        input: {
          primaryEmail: user?.primaryEmail,
          language: user?.language ?? languageName,
          organizationId: user?.organization?.id,
          type: options.defaultEntityType || EntityTypes.PrivateCompany,
        },
      },
    });

    await delay(2000); // Add 2s delay so that workflow can be created for the new entity
    await refetch(); // refetch userContext to properly populate the entity menu

    if (resp?.createUser?.user?.id) {
      // TODO: add GraphQL subscription to confirm it's ready to redirect user
      if (userContext.role?.navigationStyle === NavigationStyles.Full) {
        navigate(
          `/nonIndividualClients/${resp?.createUser?.user?.id}?autoOpenWorkflow=true`,
        );
      } else {
        const newParams = new URLSearchParams(params);
        newParams.set('autoOpenWorkflow', 'true');
        setSearchParams(newParams, { replace: true });
        setActiveEntity(resp?.createUser?.user);
      }
    }
    onNext();
  };

  return (
    <NonIndividualInformationVisual
      options={options}
      onNext={onNext}
      createNonIndividualEntity={createNonIndividualEntity}
      loading={loading || fetchLoading || createEntityLoading || stepLoading}
      workflowCompletion={workflowCompletion}
    />
  );
};

export default NonIndividualSetup;
